import React, { useContext, useEffect, useRef, useState } from 'react'
import { jwtDecode } from 'jwt-decode'
import { ejectTokenFetcher, setTokenFetcher } from 'src/utils/http.util'
import { config } from 'src/config'
import Cookies from 'js-cookie'
import type {
  ISessionData,
  ITokenSession,
  TCurrentUser,
  SessionContextValue,
  TJwtPayload,
} from '../types/session'
import { TLoginResult } from '../types/two-factor'

export const AuthContext = React.createContext<SessionContextValue>({
  isAuthenticated: false,
  isLoading: false,
  error: undefined,
})

export const COOKIES_SESSION_NAME = 'curecall-session'
export const APP_NAME = 'medical-app-' + (config.ENVIRONMENT || 'dev')
export const REDIRECT_AFTER_LOGIN_PARAM = 'next'

export const getRedirectAfterLoginPath = (search: string): string | undefined => {
  const path = new URLSearchParams(search).get(REDIRECT_AFTER_LOGIN_PARAM)
  if (!path) {
    return undefined
  }
  const isSameOriginPath = /^\/(?!\/)/.test(path) && !path.includes('://')
  return isSameOriginPath ? path : undefined
}

// Extracted so it can be mocked in tests — jsdom does not allow spying on/overriding
// `window.location.href` directly (the property is not configurable).
export const redirectTo = (url: string): void => {
  window.location.href = url
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<TCurrentUser | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const interceptorId = useRef<number | null>(null)
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined)
  const refreshTokenProcessInProgress = useRef(false)

  const getTokenInfo = (tokens: { access_token: string; refresh_token: string }): ISessionData => {
    const jwtDecodeAccessTokenData = jwtDecode<TJwtPayload>(tokens.access_token)
    return {
      currentUser: {
        id: Number(jwtDecodeAccessTokenData.sub),
        username: jwtDecodeAccessTokenData.username,
        email: jwtDecodeAccessTokenData.email,
        firstname: jwtDecodeAccessTokenData.firstname,
        lastname: jwtDecodeAccessTokenData.lastname,
        locale: jwtDecodeAccessTokenData.locale,
        role: jwtDecodeAccessTokenData.role,
        title: jwtDecodeAccessTokenData.title,
        hasMultiProjects: jwtDecodeAccessTokenData.hasMultiProjects,
        claims: jwtDecodeAccessTokenData.claims,
      },
      tokens: {
        accessToken: tokens.access_token,
        expiresAt: Number(jwtDecodeAccessTokenData.exp) * 1000,
        refreshToken: tokens.refresh_token,
      },
    }
  }

  /**
   * Obtain a refresh token lock to prevent simultaneous token refresh requests.
   */
  const obtainRefreshTokenLock = (durationMs = 10000): number | false => {
    const isLockSet = isRefreshTokenLockSet()
    if (isLockSet) {
      return false
    }

    const lock = {
      expiresAt: Date.now() + durationMs,
      id: Math.floor(Math.random() * 100000),
    }
    localStorage.setItem('refreshTokenLock', JSON.stringify(lock))
    return lock.id
  }

  /**
   * Checks if the refresh token procedure is currently locked
   */
  const isRefreshTokenLockSet = (): boolean => {
    const existingLock = localStorage?.getItem('refreshTokenLock')
    if (!existingLock) {
      return false
    }

    try {
      const lockData = JSON.parse(existingLock)
      return lockData.expiresAt > Date.now()
    } catch {
      return false
    }
  }

  /**
   * Releases the refresh token lock with the given ID.
   */
  const releaseRefreshTokenLock = (id: number): void => {
    const existingLock = localStorage?.getItem('refreshTokenLock')
    if (existingLock) {
      const lock = JSON.parse(existingLock)

      if (lock.id === id) {
        localStorage.removeItem('refreshTokenLock')
      } else {
        throw new Error('Unable to release lock. Invalid ID.')
      }
    } else {
      throw new Error('Unable to release lock. Lock does not exist.')
    }
  }

  const getFormattedUrl = (path: string) => {
    const removeSlahAtEnd = /\/?\/$/
    const removeSlahAtBeginning = /^\/?\//
    const baseUrl = import.meta.env.DEV ? '/v2' : (config.API_URL?.replace(removeSlahAtEnd, '') || '/v2')
    return `${baseUrl}/${path.replace(removeSlahAtBeginning, '')}`
  }

  const refreshToken = async (token: ITokenSession): Promise<ISessionData> => {
    const res = await fetch(getFormattedUrl('/auth/refresh-token'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        authorization: `Bearer ${token.refreshToken}`,
        'x-client-app': APP_NAME,
      },
    })

    if (![200, 201].includes(res.status)) {
      throw new Error('refreshedTokensError')
    }

    const response = await res.json()

    return getTokenInfo(response)
  }

  const getAccessToken = async () => {
    const session = await getSessionData()
    return session.tokens?.accessToken
  }

  const sleep = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * Get session data with automatic token refresh
   */
  const getSessionData = async (retryCount = 3, backoff = 500): Promise<ISessionData> => {
    // Refuse to deliver an access token while a refresh is in progress
    if (isRefreshTokenLockSet()) {
      await sleep(backoff)
      return getSessionData(retryCount--)
    }

    const tokensData = localStorage?.getItem(COOKIES_SESSION_NAME)
    const sessionData = Cookies.get(COOKIES_SESSION_NAME)
    if (!tokensData || !sessionData) {
      throw new Error('invalid session')
    }

    const tokens: ITokenSession = JSON.parse(tokensData)
    const session: ISessionData = JSON.parse(sessionData)

    if (!tokens.expiresAt) {
      throw new Error('invalid session')
    }

    if (Date.now() <= tokens?.expiresAt) {
      return {
        currentUser: session.currentUser,
        tokens: tokens,
      }
    }

    // Tokens need to be refreshed
    const lock = obtainRefreshTokenLock(10000)
    if (!lock) {
      await sleep(backoff)
      // Avoid infinite retry loops
      if (retryCount === 0) {
        throw new Error('invalid session')
      }
      return getSessionData(retryCount--)
    }

    try {
      const newSession = await refreshToken(tokens)
      setSession(newSession)
      releaseRefreshTokenLock(lock)
      return newSession
    } catch (e) {
      localStorage.removeItem(COOKIES_SESSION_NAME)
      Cookies.remove(COOKIES_SESSION_NAME)
      releaseRefreshTokenLock(lock)
      throw e
    }
  }

  const setUserWithRole = (user?: TCurrentUser) => {
    if (!user) return
    const isAdmin = user.role === 'admin'
    const isAdminOrSales = isAdmin || user.role === 'sales'
    setUser({ ...user, isAdmin, isAdminOrSales })
  }

  async function loadUserFromCookies() {
    try {
      const session = await getSessionData()
      interceptorId.current = setTokenFetcher(() => {
        return setTokenFetcherHandle(session) as Promise<string>
      })
      setUserWithRole(session.currentUser)
      setLoading(false)
      setAccessToken(session.tokens?.accessToken)
    } catch {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUserFromCookies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setSession = (session: ISessionData) => {
    localStorage.setItem(COOKIES_SESSION_NAME, JSON.stringify(session.tokens))
    Cookies.set(
      COOKIES_SESSION_NAME,
      JSON.stringify({
        currentUser: session.currentUser,
      }),
    )
  }

  const setTokenFetcherHandle = (session: ISessionData) => {
    try {
      return session.tokens && getAccessToken()
    } catch {
      return undefined
    }
  }

  const processLoginTokens = (tokens: { access_token: string; refresh_token: string }) => {
    setLoading(true)
    const session = getTokenInfo(tokens)
    setSession(session)
    interceptorId.current = setTokenFetcher(() => {
      return setTokenFetcherHandle(session) as Promise<string>
    })
    setUserWithRole(session.currentUser)
    setAccessToken(session.tokens?.accessToken)
    setLoading(false)
  }

  const login = async (credentials: { email: string; password: string }): Promise<TLoginResult> => {
    const res = await fetch(getFormattedUrl('/auth/login/'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-client-app': APP_NAME,
      },
      body: JSON.stringify({
        email: credentials?.email,
        password: credentials?.password,
      }),
    })

    if (res.status === 200 || res.status === 201) {
      const data = await res.json()

      if (data.requires2fa) {
        return {
          requires2fa: true,
          twoFactorMethod: data.twoFactorMethod,
          twoFactorToken: data.twoFactorToken,
        }
      }

      processLoginTokens(data)
      return { requires2fa: false }
    }

    if ([401, 403, 405].includes(res.status)) {
      const data = await res.json()
      throw new Error(data.message)
    }

    return { requires2fa: false }
  }

  const verifyTwoFactor = async (twoFactorToken: string, code: string) => {
    const res = await fetch(getFormattedUrl('/auth/2fa/verify-login'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-client-app': APP_NAME,
      },
      body: JSON.stringify({ twoFactorToken, code }),
    })

    if (res.status === 201 || res.status === 200) {
      const tokens = await res.json()
      processLoginTokens(tokens)
      return
    }

    if (res.status === 403) {
      const data = await res.json()
      if (data.message === 'TWO_FACTOR_LOCKED') {
        throw new Error('TWO_FACTOR_LOCKED')
      }
    }

    if ([401, 403].includes(res.status)) {
      throw new Error('INVALID_CODE')
    }

    throw new Error('VERIFICATION_FAILED')
  }

  const completeLifenConnectLogin = async () => {
    const res = await fetch(getFormattedUrl('/auth/oidc/session-exchange'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-client-app': APP_NAME },
    })

    if (!res.ok) {
      throw new Error('LIFEN_CONNECT_SESSION_EXCHANGE_FAILED')
    }

    processLoginTokens(await res.json())
  }

  const refreshSession = async () => {
    try {
      const session = await getSessionData()
      setAccessToken(session.tokens?.accessToken)
    } catch {
      logout({ isAutomatic: true })
    }
  }

  const logout = ({ isAutomatic = false }: { isAutomatic?: boolean } = {}) => {
    setLoading(true)
    const redirectPath = isAutomatic ? window.location.pathname + window.location.search : undefined
    localStorage.removeItem(COOKIES_SESSION_NAME)
    Cookies.remove(COOKIES_SESSION_NAME)
    setUser(undefined)
    interceptorId.current !== null && ejectTokenFetcher(interceptorId.current)
    setLoading(false)
    redirectTo(
      redirectPath ? `/login?${REDIRECT_AFTER_LOGIN_PARAM}=${encodeURIComponent(redirectPath)}` : '/login',
    )
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        currentUser: user,
        login,
        verifyTwoFactor,
        completeLifenConnectLogin,
        isLoading: loading || refreshTokenProcessInProgress.current,
        logout,
        refreshSession,
        accessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useSession = () => useContext(AuthContext)
